/**
 * -----------------------------------------------------------------------------
 * Vehicle Dynamic Input Labels
 * -----------------------------------------------------------------------------
 * A collapsible section of the vehicle admin page, closed by default like the
 * others, headed "Dynamic Input Labels: n of n set". Both numbers come from the
 * API's list of the asset's labels, and the section lists a field for every
 * label in that list. Edits are kept here and saved by the page's Update button
 * through the handle, one PUT per changed label: labels that fail stay marked
 * in the section and the rest are saved. Fields are editable only with device
 * edit rights while the page is in Edit mode, as in the other sections.
 */
import { forwardRef, useImperativeHandle, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  usePutVehicleDynamicPtoLabelMutation,
  useVehicleDynamicPtoLabelsQuery,
} from "../../../../api/network/adminApiServices";
import { VehicleDynamicPtoLabel } from "../../../../api/types/Vehicle";
import { AdminFormFieldInput } from "../../../../components/admin/formFields";
import Accordian from "../../../../components/accordian";
import {
  DYNAMIC_INPUT_LABEL_MAX_LENGTH,
  EditedDynamicInputLabels,
  changedDynamicInputLabels,
  displayedDynamicInputLabelText,
  dynamicInputLabelIsTooLong,
  editedDynamicInputLabelsAreValid,
  setDynamicInputLabelCount,
} from "../dynamic-pto-labels";

const TRANSLATION_PREFIX = "admins.vehicles.detailsPage.dynamic_input_labels";

export interface VehicleDynamicInputLabelsHandle {
  // false (with an error toast) when an edit breaks the label rules
  validateChanges: () => boolean;
  // rejects, shaped like an API error, when any label could not be saved
  saveChanges: () => Promise<void>;
}

type VehicleDynamicInputLabelsProps = {
  deviceId: string;
  // device edit rights and the page in Edit mode
  canEdit: boolean;
  // told when label saves start and finish, so the page can block Update meanwhile
  onSavingChange: (labelsAreSaving: boolean) => void;
};

export const VehicleDynamicInputLabels = forwardRef<
  VehicleDynamicInputLabelsHandle,
  VehicleDynamicInputLabelsProps
>(function VehicleDynamicInputLabelsSection({ deviceId, canEdit, onSavingChange }, ref) {
  const { t } = useTranslation("translation", { keyPrefix: TRANSLATION_PREFIX });
  const { data: storedLabels, error: storedLabelsError } =
    useVehicleDynamicPtoLabelsQuery(deviceId);
  const [saveLabelTrigger] = usePutVehicleDynamicPtoLabelMutation();

  const [editedLabels, setEditedLabels] = useState<EditedDynamicInputLabels>({});
  const [failedLabelNumbers, setFailedLabelNumbers] = useState<number[]>([]);
  const [labelsAreSaving, setLabelsAreSaving] = useState(false);

  useImperativeHandle(ref, () => ({
    validateChanges: () => {
      const editsAreValid = editedDynamicInputLabelsAreValid(editedLabels);
      if (!editsAreValid) {
        // names the section, whose inline error may be out of sight while it is collapsed
        toast.error(t("too_long_toast", { maxLength: DYNAMIC_INPUT_LABEL_MAX_LENGTH }));
      }
      return editsAreValid;
    },

    saveChanges: async () => {
      // nothing can have been edited before the labels loaded
      if (storedLabels === undefined) return;
      const labelsToSave = changedDynamicInputLabels(storedLabels, editedLabels);
      if (labelsToSave.length === 0) return;

      setLabelsAreSaving(true);
      onSavingChange(true);
      let saveResults: PromiseSettledResult<unknown>[];
      try {
        saveResults = await Promise.allSettled(
          labelsToSave.map((labelToSave) =>
            saveLabelTrigger({ device_id: deviceId, ...labelToSave }).unwrap()
          )
        );
      } finally {
        setLabelsAreSaving(false);
        onSavingChange(false);
      }

      const labelNumbersThatFailed = labelsToSave
        .filter((labelToSave, saveIndex) => saveResults[saveIndex].status === "rejected")
        .map((labelToSave) => labelToSave.label_number);
      setFailedLabelNumbers(labelNumbersThatFailed);
      if (labelNumbersThatFailed.length > 0) {
        // the saved labels keep their typed text, which matches what is now
        // stored; the page's save handler shows this message and stays open
        const someFailedMessage = t("some_failed");
        throw Object.assign(new Error(someFailedMessage), {
          data: { non_field_errors: [someFailedMessage] },
        });
      }
    },
  }));

  const levelText = (level: VehicleDynamicPtoLabel["level"]) =>
    level === "High" ? t("level_high") : t("level_low");

  const fieldError = (labelNumber: number, labelText: string) => {
    if (dynamicInputLabelIsTooLong(labelText)) {
      return t("too_long", { maxLength: DYNAMIC_INPUT_LABEL_MAX_LENGTH });
    }
    if (failedLabelNumbers.includes(labelNumber)) {
      return t("label_failed");
    }
    return "";
  };

  // plain text, like every other section title, so the header renders the same
  const sectionTitle =
    storedLabels === undefined
      ? t("title")
      : `${t("title")}: ${t("set_count", {
          setCount: setDynamicInputLabelCount(storedLabels),
          totalCount: storedLabels.length,
        })}`;

  const renderSectionContent = () => {
    // once loaded, the fields stay even if a later refetch fails, so edits are never hidden
    if (storedLabels === undefined) {
      if (storedLabelsError) {
        return (
          <div className="col-span-12 text-sm text-gray-700">
            {"status" in storedLabelsError && storedLabelsError.status === 403
              ? t("no_permission")
              : t("load_failed")}
          </div>
        );
      }
      return <div className="col-span-12 text-sm text-gray-500">{t("loading")}</div>;
    }
    return (
      <>
        {canEdit && <p className="col-span-12 text-sm text-gray-700">{t("description")}</p>}
        {storedLabels.map((storedLabel) => {
          const labelText = displayedDynamicInputLabelText(
            storedLabels,
            editedLabels,
            storedLabel.label_number
          );
          return (
            <AdminFormFieldInput
              key={storedLabel.label_number}
              label={t("field_label", {
                inputNumber: storedLabel.input_number,
                level: levelText(storedLabel.level),
              })}
              type="text"
              id={`dynamic_input_label_${storedLabel.label_number}`}
              name={`dynamic_input_label_${storedLabel.label_number}`}
              value={labelText}
              onChange={(changeEvent) => {
                const typedLabelText = changeEvent.target.value;
                setEditedLabels((previousEditedLabels) => ({
                  ...previousEditedLabels,
                  [storedLabel.label_number]: typedLabelText,
                }));
                setFailedLabelNumbers((previousFailedLabelNumbers) =>
                  previousFailedLabelNumbers.filter(
                    (failedLabelNumber) => failedLabelNumber !== storedLabel.label_number
                  )
                );
              }}
              error={fieldError(storedLabel.label_number, labelText)}
              disabled={!canEdit || labelsAreSaving}
              detailsFormField={true}
              customWrapperClass="col-span-12 sm:col-span-6"
            />
          );
        })}
      </>
    );
  };

  return (
    // kept mounted while collapsed so edits survive closing the section
    <Accordian title={sectionTitle} keepMounted={true}>
      <div className="px-5 pt-4 pb-8 bg-gray-100 grid grid-cols-12 gap-4">
        {renderSectionContent()}
      </div>
    </Accordian>
  );
});
